<template>
<section class="relative overflow-hidden max-w-[1152px] mx-auto font-open-sans">

    
    <div class="relative z-10 pb-10 lg:px-10 lg:pt-16 lg:pb-16">

        

        <div class="grid grid-cols-12 lg:gap-x-6">
            <div class="grid items-center order-1 col-span-12 px-4 lg:col-span-5 lg:order-2 lg:px-0 md:px-10">
                <div class="">
                    <h1 class="mt-6 font-bold !leading-8 text-center text-2xl xl:text-3xl lg:mt-0 lg:text-left">
                        {{ title }}
                    </h1>

                    <div class="max-w-md mx-auto lg:max-w-auto">
                        <h2 class="mx-auto mt-3 text-4xl font-bold text-center xl:text-40 w-fit lg:mx-0">
                            {{ discountText }}
 
                            <span class="relative bottom-0 xl:bottom-0">
                                <nuxt-img 
                                    :src="underlineImage" 
                                    class="w-72 xl:w-full" 
                                    width="311"
                                    height="15" 
                                    preload 
                                    loading="eager" 
                                    fetchpriority="high" 
                                    format="webp"
                                    sizes="xs:330px sm:440px md:600px lg:720px xl:584px" 
                                    :alt="underlineAlt" 
                                />
                            </span>
                        </h2>

                        <div class="lg:text-xl text-lg tracking-[6px] mt-2 lg:text-left text-center">
                            {{ limitedTimeText }}
                        </div>

                        <div class="mt-2 ml-12 space-y-1 lg:ml-6">
                            <div 
                                v-for="(feature, index) in features" 
                                :key="index"
                                class="flex items-center gap-x-2"
                            >
                                <div class="flex items-center justify-center w-4 h-4 bg-green-500 rounded-full">
                                    <svg class="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <span class="text-base font-medium">
                                    {{ feature.feature || feature }}
                                </span>
                            </div>
                        </div>

                        <div class="flex flex-col items-center justify-start mt-6 space-y-6 lg:mt-4 lg:space-y-8 lg:items-start md:mt-4">
                            <div class="text-center lg:px-0 sm:px-4">
                                <button 
                                    @click="scrollTo(buttonLink, scrollPosition('bottom'))"
                                    class="justify-center flex items-center w-full md:w-auto font-bold bg-[#A53B38] text-white text-lg sm:text-xl lg:text-2xl px-3 sm:px-4 lg:px-8 py-2 rounded-2xl"
                                    style="box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 4px 4px rgba(0, 0, 0, 0.25);"
                                >
                                    {{ buttonText }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex items-center col-span-12 lg:col-span-7 lg:order-2">
                <video 
                    autoplay 
                    muted 
                    playsinline 
                    loop 
                    preload="auto" 
                    width="1920" 
                    height="1080"
                    :poster="videoPosterDesktop"
                    class="hidden object-cover shadow-lg lg:rounded-3xl md:block"
                >
                    <source :src="videoDesktop" type="video/mp4">
                    Your browser does not support the video tag.
                </video>

                <video 
                    autoplay 
                    muted 
                    playsinline 
                    loop 
                    preload="auto" 
                    width="854" 
                    height="480"
                    :poster="videoPosterMobile"
                    class="object-cover shadow-lg lg:rounded-3xl md:hidden"
                >
                    <source :src="videoMobile" type="video/mp4">
                    Your browser does not support the video tag.
                </video>
            </div>
        </div>

        <div class="flex flex-col items-center justify-center px-4 mt-6 lg:mt-3 lg:justify-start gap-x-3 lg:px-0 md:flex-row">
            <div class="">
                <nuxt-img 
                    :src="starsImage" 
                    :alt="starsAlt" 
                    class="mx-auto"
                    width="125" 
                    height="22" 
                    preload 
                    format="webp" 
                />
            </div>
            <div class="text-base font-bold tracking-[2px] lg:mt-0 mt-2 lg:text-left text-center">
                {{ testimonialText }}
            </div>
        </div>
    </div>

    <div class="absolute inset-0 hidden object-cover w-full md:block">
        <nuxt-img 
            :src="backgroundDesktop"
            class="absolute object-cover w-full h-full lg:object-cover" 
            width="1911" 
            height="835" 
            preload
            loading="eager" 
            fetchpriority="high" 
            format="webp"
            sizes="xs:330px sm:440px md:768px lg:1024px xl:1200px" 
            :alt="backgroundDesktopAlt" 
        />
    </div>

    <div class="absolute inset-0 object-cover w-full md:hidden">
        <nuxt-img 
            :src="backgroundMobile"
            class="absolute bottom-0 w-full" 
            width="620" 
            height="870" 
            preload
            loading="eager" 
            fetchpriority="high" 
            format="webp" 
            sizes="xs:330px sm:440px md:600px lg:720px xl:584px"
            :alt="backgroundMobileAlt" 
        />
    </div>
</section>
</template>

<script setup>
const props = defineProps({
  title: {
    type: String,
    default: "Secure Your Supply of Our Delicious, Immune-Boosting, Low-Acid Healthy Coffee",
  },
  discountText: {
    type: String,
    default: "58% OFF Today!",
  },
  limitedTimeText: {
    type: String,
    default: "WHILE SUPPLIES LAST",
  },
  features: {
    type: Array,
    default: () => [
      "USDA Certified Organic",
      "Low-Acid & Stomach-Friendly",
      "Tested for 450+ Toxins",
      "Shade-Grown at High Elevations",
      "Responsibly Sourced"
    ],
  },
  buttonText: {
    type: String,
    default: "Limited time: TRY IT NOW!",
  },
  buttonLink: {
    type: String,
    default: "the-products",
  },
  videoDesktop: {
    type: String,
    default: "https://cdn.shopify.com/videos/c/o/v/83fea662343d4fd6a90949c9b0cc4f72.mp4",
  },
  videoMobile: {
    type: String,
    default: "https://cdn.shopify.com/videos/c/o/v/4bd247cf401d43c1ba9f3635ca821e39.mp4",
  },
  videoPosterDesktop: {
    type: String,
    default: "/assets/images/poster-desktop-hero.png",
  },
  videoPosterMobile: {
    type: String,
    default: "/assets/images/poster-desktop-hero.png",
  },
  underlineImage: {
    type: String,
    default: "/assets/images/bottom-line-head-text.png",
  },
  underlineAlt: {
    type: String,
    default: "hero underline",
  },
  starsImage: {
    type: String,
    default: "/assets/images/stars-yellow.png",
  },
  starsAlt: {
    type: String,
    default: "stars",
  },
  testimonialText: {
    type: String,
    default: "Over 60,000,000 Cups Served of Lifeboost Coffee",
  },
  backgroundDesktop: {
    type: String,
    default: "/assets/images/bg-header-first-time.jpg",
  },
  backgroundDesktopAlt: {
    type: String,
    default: "hero background",
  },
  backgroundMobile: {
    type: String,
    default: "/assets/images/bg-mobile-bottom.png",
  },
  backgroundMobileAlt: {
    type: String,
    default: "hero mobile background",
  },
});

defineBuilderComponent({
  friendlyName: "HeroTitleDescriptionVideo",
  inputs: [
    {
      name: "title",
      type: "string",
      friendlyName: "Main Title",
      defaultValue: "Secure Your Supply of Our Delicious, Immune-Boosting, Low-Acid Healthy Coffee",
    },
    {
      name: "discountText",
      type: "string",
      friendlyName: "Discount Text",
      defaultValue: "58% OFF Today!",
    },
    {
      name: "limitedTimeText",
      type: "string",
      friendlyName: "Limited Time Text",
      defaultValue: "WHILE SUPPLIES LAST",
    },
    {
      name: "features",
      type: "list",
      subFields: [
        {
          name: "feature",
          type: "string",
          friendlyName: "Feature Text",
        }
      ],
      friendlyName: "Product Features",
      defaultValue: [
        { feature: "USDA Certified Organic" },
        { feature: "Low-Acid & Stomach-Friendly" },
        { feature: "Tested for 450+ Toxins" },
        { feature: "Shade-Grown at High Elevations" },
        { feature: "Responsibly Sourced" }
      ],
    },
    {
      name: "buttonText",
      type: "string",
      friendlyName: "CTA Button Text",
      defaultValue: "Limited time: TRY IT NOW!",
    },
    {
      name: "buttonLink",
      type: "string",
      friendlyName: "CTA Button Link/ID",
      defaultValue: "the-products",
    },
    {
      name: "videoDesktop",
      type: "url",
      friendlyName: "Desktop Video URL",
      defaultValue: "https://cdn.shopify.com/videos/c/o/v/83fea662343d4fd6a90949c9b0cc4f72.mp4",
    },
    {
      name: "videoMobile",
      type: "url",
      friendlyName: "Mobile Video URL",
      defaultValue: "https://cdn.shopify.com/videos/c/o/v/4bd247cf401d43c1ba9f3635ca821e39.mp4",
    },
    {
      name: "videoPosterDesktop",
      type: "file",
      friendlyName: "Desktop Video Poster",
      allowedFileTypes: ["jpg", "jpeg", "png", "webp"],
      defaultValue: "/assets/images/poster-desktop-hero.png",
    },
    {
      name: "videoPosterMobile",
      type: "file",
      friendlyName: "Mobile Video Poster",
      allowedFileTypes: ["jpg", "jpeg", "png", "webp"],
      defaultValue: "/assets/images/poster-desktop-hero.png",
    },
    {
      name: "underlineImage",
      type: "file",
      friendlyName: "Underline Decoration Image",
      allowedFileTypes: ["jpg", "jpeg", "png", "webp"],
      defaultValue: "/assets/images/bottom-line-head-text.png",
    },
    {
      name: "underlineAlt",
      type: "string",
      friendlyName: "Underline Image Alt Text",
      defaultValue: "hero underline",
    },
    {
      name: "starsImage",
      type: "file",
      friendlyName: "Stars Rating Image",
      allowedFileTypes: ["jpg", "jpeg", "png", "webp"],
      defaultValue: "/assets/images/stars-yellow.png",
    },
    {
      name: "starsAlt",
      type: "string",
      friendlyName: "Stars Image Alt Text",
      defaultValue: "stars",
    },
    {
      name: "testimonialText",
      type: "string",
      friendlyName: "Testimonial/Stats Text",
      defaultValue: "Over 60,000,000 Cups Served of Lifeboost Coffee",
    },
    {
      name: "backgroundDesktop",
      type: "file",
      friendlyName: "Desktop Background Image",
      allowedFileTypes: ["jpg", "jpeg", "png", "webp"],
      defaultValue: "/assets/images/bg-header-first-time.jpg",
    },
    {
      name: "backgroundDesktopAlt",
      type: "string",
      friendlyName: "Desktop Background Alt Text",
      defaultValue: "hero background",
    },
    {
      name: "backgroundMobile",
      type: "file",
      friendlyName: "Mobile Background Image",
      allowedFileTypes: ["jpg", "jpeg", "png", "webp"],
      defaultValue: "/assets/images/bg-mobile-bottom.png",
    },
    {
      name: "backgroundMobileAlt",
      type: "string",
      friendlyName: "Mobile Background Alt Text",
      defaultValue: "hero mobile background",
    },
  ],
});
</script>